import React from 'react'
import { Box, Collapse, Stack, Typography } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import type { HolidayItineraryDay, HolidayImage } from '@/src/types/holidays'

import HolidayImageView from '../holidayImage'
import ImageCarouselWrapper from '../imageCarousel'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

export interface ItineraryDayView extends HolidayItineraryDay {
    /** The title split into its stops, or empty when the title names only one. */
    stops: string[]
    /** First image of the day, shown in the header so a collapsed day is not text alone. */
    thumbnail?: HolidayImage
}

interface ItineraryTimelineProps {
    days: ItineraryDayView[]
    /** Index of the day currently expanded, or -1 when all are collapsed. */
    expandedIndex: number
    onToggleDay: (index: number) => void
    /** A day picked from the rail: open it and bring it into view. */
    onSelectDay: (index: number) => void
    /** Fired once a day has finished opening. */
    onDayExpanded: (index: number) => void
    /** Fired once a day has finished closing, which is when the page above has stopped moving. */
    onDayCollapsed: () => void
    /** Heading above the day rail. */
    railTitle: string
    isMobile: boolean
}

const ItineraryTimeline: React.FC<ItineraryTimelineProps> = ({
    days,
    expandedIndex,
    onToggleDay,
    onSelectDay,
    onDayExpanded,
    onDayCollapsed,
    railTitle,
    isMobile
}) => {
    return (
        <Stack spacing={1.25}>
            {/*
             * The rail is the whole trip at a glance and a way into any day of it. Without it a
             * nine-day itinerary can only be reached by scrolling past every day before it.
             */}
            {days.length > 1 && (
                <Stack spacing={1.25} sx={{ mb: 0.25 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography
                            sx={{
                                flexShrink: 0,
                                fontSize: isMobile ? '0.938rem' : '1rem',
                                fontWeight: 700,
                                color: 'textColors.primary'
                            }}
                        >
                            {railTitle}
                        </Typography>
                        <AutoAwesomeIcon sx={{ fontSize: 14, flexShrink: 0, color: 'primary.main' }} />
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'borders.default' }} />
                    </Stack>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            overflowX: 'auto',
                            pb: 0.5,
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' }
                        }}
                    >
                        {days.map((day, index) => {
                            const isActive = index === expandedIndex

                            return (
                                <Box
                                    key={`rail-${day.dayLabel}-${index}`}
                                    component="button"
                                    type="button"
                                    onClick={() => onSelectDay(index)}
                                    aria-current={isActive}
                                    sx={{
                                        font: 'inherit',
                                        flexShrink: 0,
                                        px: 1.75,
                                        py: 0.75,
                                        borderRadius: 50,
                                        border: '1px solid',
                                        borderColor: isActive ? 'textColors.primary' : 'borders.default',
                                        bgcolor: isActive ? 'neutral.100' : 'background.paper',
                                        color: 'textColors.primary',
                                        fontSize: isMobile ? '1rem' : '0.875rem',
                                        fontWeight: isActive ? 700 : 500,
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer',
                                        '&:hover': { borderColor: 'textColors.primary' }
                                    }}
                                >
                                    {day.dayLabel}
                                </Box>
                            )
                        })}
                    </Box>
                </Stack>
            )}
            {days.map((day, index) => {
                const isExpanded = index === expandedIndex
                const panelId = `itinerary-day-panel-${index}`

                return (
                    <Box
                        key={day.dayLabel + day.title}
                        id={`itinerary-day-${index}`}
                        sx={{
                            // Clears the sticky section bar, matching what the section headings use.
                            scrollMarginTop: isMobile ? '132px' : '124px',
                            borderRadius: HOLIDAYS_RADIUS.NESTED,
                            border: '1px solid',
                            borderColor: isExpanded ? 'primary.main' : 'borders.default',
                            bgcolor: 'background.paper',
                            overflow: 'hidden',
                            transition: 'border-color 0.2s'
                        }}
                    >
                        <Box
                            component="button"
                            type="button"
                            onClick={() => onToggleDay(index)}
                            aria-expanded={isExpanded}
                            aria-controls={panelId}
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: isMobile ? 1.25 : 2,
                                textAlign: 'start',
                                border: 'none',
                                bgcolor: 'transparent',
                                cursor: 'pointer',
                                px: isMobile ? 1.5 : 2,
                                py: isMobile ? 1.5 : 1.75,
                                font: 'inherit',
                                color: 'inherit',
                                '&:hover': { bgcolor: 'neutral.100' }
                            }}
                        >
                            {day.thumbnail && (
                                <Box
                                    sx={{
                                        width: isMobile ? 52 : 64,
                                        height: isMobile ? 52 : 64,
                                        flexShrink: 0,
                                        borderRadius: HOLIDAYS_RADIUS.NESTED,
                                        overflow: 'hidden',
                                        bgcolor: 'neutral.100'
                                    }}
                                >
                                    <HolidayImageView image={day.thumbnail} isMobile={isMobile} />
                                </Box>
                            )}

                            <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                                <Box
                                    sx={{
                                        alignSelf: 'flex-start',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 50,
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        fontSize: '0.688rem',
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    {day.dayLabel}
                                </Box>

                                {/*
                                 * Stops carry the same words as the title, split on the pipes the
                                 * editor typed. A day whose title names only one stop keeps the
                                 * plain heading rather than becoming a lone chip.
                                 */}
                                {day.stops.length ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.625 }}>
                                        {day.stops.map(stop => (
                                            <Typography
                                                key={stop}
                                                sx={{
                                                    px: 0.875,
                                                    py: 0.25,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'borders.default',
                                                    bgcolor: 'neutral.100',
                                                    fontSize: isMobile ? '0.75rem' : '1rem',
                                                    fontWeight: 600,
                                                    lineHeight: 1.45,
                                                    color: 'textColors.subtitle'
                                                }}
                                            >
                                                {stop}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography
                                        sx={{
                                            fontSize: isMobile ? '0.875rem' : '0.938rem',
                                            fontWeight: 600,
                                            lineHeight: 1.45,
                                            color: 'textColors.primary'
                                        }}
                                    >
                                        {day.title}
                                    </Typography>
                                )}
                            </Stack>

                            <ExpandMoreIcon
                                sx={{
                                    flexShrink: 0,
                                    fontSize: 22,
                                    color: 'textColors.subheading',
                                    transition: 'transform 0.2s',
                                    transform: isExpanded ? 'rotate(180deg)' : 'none'
                                }}
                            />
                        </Box>

                        <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit
                            onEntered={() => onDayExpanded(index)}
                            onExited={onDayCollapsed}
                        >
                            <Stack
                                component="ul"
                                id={panelId}
                                spacing={0.875}
                                sx={{
                                    m: 0,
                                    listStyle: 'none',
                                    px: isMobile ? 1.5 : 2,
                                    pb: isMobile ? 1.75 : 2,
                                    pt: 0.5,
                                    pl: isMobile ? 1.5 : 2
                                }}
                            >
                                {day.highlights.map(highlight => (
                                    <Box
                                        key={highlight}
                                        component="li"
                                        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                                    >
                                        <Box
                                            sx={{
                                                width: 5,
                                                height: 5,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                                mt: '0.55em',
                                                flexShrink: 0
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: isMobile ? '1rem' : '0.875rem',
                                                lineHeight: 1.65,
                                                color: 'textColors.secondary'
                                            }}
                                        >
                                            {highlight}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>

                            {day.images && day.images.length > 0 && (
                                <Box sx={{ px: isMobile ? 1.5 : 2, pb: isMobile ? 1.75 : 2 }}>
                                    {/*
                                     * A phone gets one slide at a time; a desktop has the width to
                                     * show the day's images side by side and gains nothing from
                                     * hiding two thirds of them behind a timer.
                                     */}
                                    {isMobile ? (
                                        <ImageCarouselWrapper images={day.images} height={190} />
                                    ) : (
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: `repeat(${Math.min(
                                                    day.images.length,
                                                    3
                                                )}, 1fr)`,
                                                gap: 1
                                            }}
                                        >
                                            {day.images.map(image => (
                                                <Box
                                                    key={image.url}
                                                    sx={{
                                                        height: 170,
                                                        borderRadius: HOLIDAYS_RADIUS.NESTED,
                                                        overflow: 'hidden',
                                                        bgcolor: 'neutral.100'
                                                    }}
                                                >
                                                    <HolidayImageView image={image} isMobile={false} />
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Collapse>
                    </Box>
                )
            })}
        </Stack>
    )
}

export { ItineraryTimeline }
export type { ItineraryTimelineProps }
export default ItineraryTimeline
